/**
 * B3 — the one place the "Limit connection to selected database" rule lives.
 *
 * The rule was duplicated across five sites (the renderer's central
 * `getDatabases` filter, the Object Explorer folder expansion, the Object
 * Explorer selection coercion, the AI-workspace consent chokepoint, the
 * main-process Job Scheduler listing) plus two inline `limitToDatabase && db`
 * checks in the error-fallback paths. Each copy re-implemented the same three
 * decisions — flag check, declared-database fallback, trim — and one of the
 * satellites also compared database names case-SENSITIVELY, disagreeing with
 * the central filter ([CP2-DEFER-3]).
 *
 * Everything here is pure (type-only imports) so the Electron main process can
 * import it too — same pattern as `connectionPoolReset` / `certificateTrustError`.
 *
 * Database-name comparison is case-insensitive on purpose: SQL Server
 * identifiers are case-insensitive by default, and a user who typed `dbcam`
 * into the connection form must match the server's `DBCAM`. This is UI/export
 * scoping only — it never gates the SQL a user writes.
 */

import type { Connection } from '../../../types/electron';

/** The minimum a connection needs to carry for the limit rule to apply. */
export type ConnectionDatabaseLimit = Pick<Connection, 'limitToDatabase' | 'database'>;

/**
 * The trimmed database this connection is scoped to, or `undefined` when it
 * isn't limited (flag off, or flag on with an empty/whitespace database).
 * `undefined` ⇒ no scoping, so unlimited connections behave byte-identically.
 */
export function getLimitedDatabase(
  connection: ConnectionDatabaseLimit | undefined,
): string | undefined {
  if (!connection?.limitToDatabase) return undefined;
  return connection.database?.trim() || undefined;
}

/**
 * Case-insensitive database-name equality (trim + lowercase). Empty/undefined
 * on either side is never equal.
 */
export function isSameDatabaseName(
  a: string | undefined,
  b: string | undefined,
): boolean {
  const left = a?.trim().toLowerCase();
  const right = b?.trim().toLowerCase();
  return Boolean(left && right && left === right);
}

/**
 * Find `wanted` in a listing, returning the LISTED name (so the server's own
 * casing wins downstream) or `undefined` when it isn't there.
 *
 * An exact match is preferred over a case-insensitive one, so engines whose
 * database names really are case-sensitive (PostgreSQL) still resolve to the
 * exact database when it is present in the listing.
 */
export function findDatabaseInList(
  names: readonly string[] | undefined,
  wanted: string | undefined,
): string | undefined {
  if (!names || !wanted) return undefined;
  const exact = names.find((name) => name === wanted);
  if (exact !== undefined) return exact;
  return names.find((name) => isSameDatabaseName(name, wanted));
}

/**
 * Scope a listed set of database names to a single limited database:
 * case-insensitive match, keeping the LISTED casing when one exists; when the
 * limited database is absent from the listing (permissions / offline listing
 * quirk) fall back to the user's declared name — connecting to it surfaces the
 * real error downstream.
 */
export function applyDatabaseLimit(names: string[], target: string): string[] {
  const matched = names.filter((name) => isSameDatabaseName(name, target));
  return matched.length > 0 ? matched : [target];
}

/**
 * "Would `getDatabases` return something different for this connection after
 * this edit?" — i.e. must the shared database-listing cache entry
 * (`databaseObjectsQueryKey.databases(connectionId)`) be dropped?
 *
 * Only two saved fields reach that listing: `limitToDatabase` (applied inside
 * `getDatabases` via {@link applyDatabaseLimit}) and `database` (the limit
 * target, and the head of the fallback list on a failed listing). A cached
 * entry written before the edit would paint the pre-edit list — and the pickers
 * now paint cached rows BEFORE their revalidation lands — so the entry has to
 * be REMOVED, not merely invalidated.
 *
 * Deliberately narrow: `knownDatabasesTracker` fires an `updateConnection` ~1s
 * after connect to append `knownDatabases`, exactly when the user first opens a
 * picker. Keying on the two fields that matter means that write cannot thrash
 * the cache.
 */
export function connectionEditChangesDatabaseListing(
  prior: ConnectionDatabaseLimit | undefined,
  next: ConnectionDatabaseLimit,
): boolean {
  if (!prior) return false;
  if (Boolean(prior.limitToDatabase) !== Boolean(next.limitToDatabase)) return true;
  return (prior.database?.trim() ?? '') !== (next.database?.trim() ?? '');
}

/**
 * Coerce a requested database to a connection's limit target.
 *
 * Any requested database that is not case-insensitively equal to the limited
 * one is replaced with the limited database (keeping the requested casing when
 * it matches). Unlimited connections — and limited connections with no
 * configured database — return the requested value untouched.
 *
 * Closes the AI-workspace export gate: an off-limit pinned database node must
 * never seed the export, whether the consent dialog is shown (it seeds the
 * picker) or skipped (the terminal is created immediately).
 */
export function coerceDatabaseToConnectionLimit(
  connection: ConnectionDatabaseLimit | undefined,
  requested: string | undefined,
): string | undefined {
  const target = getLimitedDatabase(connection);
  if (!target) return requested;
  return isSameDatabaseName(requested, target) ? requested : target;
}
